import { supabaseClient } from '../../utils/supabase';

/** One of the merchant's deal cycles as the edge function describes it. */
export interface DealPrepCycleOption {
  id: number;
  cycle_sequence: number;
  label: string;
  submissionCount: number;
}

/** Response of deal-prep-history-email for both preview and send. */
export interface DealPrepHistoryResult {
  ok: boolean;
  preview: boolean;
  /** email_send id (send only). */
  id?: number;
  /** Rendered email body (preview only). */
  html?: string;
  merchantId: number;
  businessName: string;
  subject: string;
  /** "<Main rep name> <login@skylinecaps.com>", or Skyline Capital <admin@> when the main rep has no login or is an admin sender. */
  from: string;
  fromFallback: boolean;
  /** admin@ sends because the main rep is one of global_setting.deal_prep_admin_sender_emails (Zack / David). */
  fromAdminSender?: boolean;
  adminSenderEmails?: string[];
  to: string[];
  /** admin@ + the main rep + the merchant's additional reps (merchant-wide rep_links). */
  cc: string[];
  mainRep: { id: number; name: string; email: string | null } | null;
  additionalReps: { id: number; name: string; email: string | null }[];
  scope: { allCycles: boolean; dealCycleId: number | null };
  submissionCount: number;
  submissionNumbers: number[];
  cycles: DealPrepCycleOption[];
  unassignedCount: number;
}

export interface DealPrepHistoryScope {
  /** true = every cycle; false = one cycle (dealCycleId, else the most recent). */
  allCycles: boolean;
  dealCycleId: number | null;
}

/**
 * REG-721: the edge function builds the history from the database (no rows
 * are pulled into the browser), enforces the send_deal_prep_history
 * capability, and either returns the rendered email (preview) or queues it
 * into the merchant's DEAL PREP chain.
 */
export async function requestDealPrepHistory(opts: {
  merchantId: number;
  scope: DealPrepHistoryScope;
  preview: boolean;
}): Promise<{ ok: true; result: DealPrepHistoryResult } | { ok: false; error: string }> {
  const { data, error } = await supabaseClient.functions.invoke('deal-prep-history-email', {
    body: {
      merchantId: opts.merchantId,
      allCycles: opts.scope.allCycles,
      dealCycleId: opts.scope.allCycles ? null : opts.scope.dealCycleId,
      preview: opts.preview,
    },
  });
  if (error) {
    // supabase-js wraps non-2xx responses; surface the server's message.
    const context = (error as { context?: Response }).context;
    if (context) {
      const payload = await context.json().catch(() => null);
      if (payload?.error) return { ok: false, error: String(payload.error) };
    }
    return { ok: false, error: error.message ?? 'The email service rejected the request' };
  }
  if (!data?.ok) return { ok: false, error: data?.error ?? 'Empty response from the email service' };
  return { ok: true, result: data as DealPrepHistoryResult };
}
